import { UzCombobox } from '@utazzo/ui';

import { usePickerOptions } from './usePickerOptions';

import * as citiesApi from '@/features/cities/cities.api';

interface CityPickerProps {
  label?: string;
  value: string | null;
  onValueChange: (value: string | null) => void;
  /** Hard country scoping. When set, only cities in that country are searched. */
  countryId?: string | null;
  /**
   * Soft country preference. The default (no-search) list shows this country's
   * cities, but typing a search escapes the filter and matches cities anywhere.
   * Ignored when `countryId` (hard scope) is set.
   */
  preferCountryId?: string | null;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  hint?: string;
  placeholder?: string;
}

export const CityPicker = ({
  label = 'City',
  value,
  onValueChange,
  countryId,
  preferCountryId,
  required,
  disabled,
  error,
  hint,
  placeholder = 'Select a city',
}: CityPickerProps): React.JSX.Element => {
  const { options, loading, setSearch } = usePickerOptions(
    {
      cacheKey: `picker.cities.${countryId ?? 'any'}.${preferCountryId ?? 'any'}`,
      fetch: async (search) => {
        // Hard scope always filters by countryId. Soft preference filters the
        // default list only; a search term matches cities globally.
        const scopeCountryId = countryId ?? (search ? undefined : preferCountryId);
        const res = await citiesApi.listCities({
          page: 1,
          pageSize: 30,
          sortDir: 'asc',
          isActive: true,
          ...(search ? { search, searchMode: 'prefix' as const } : {}),
          ...(scopeCountryId ? { countryId: scopeCountryId } : {}),
        });
        return res.data;
      },
      fetchOne: async (id) => {
        try {
          const d = await citiesApi.getCity(id);
          return d;
        } catch {
          return null;
        }
      },
      toOption: (c) => {
        // 2026-07-05. Format: "Srinagar (SXR), India" - name + IATA code in
        // round brackets (matching IataCityPicker) + country name after a
        // comma. Each part is dropped when absent. State stays on the
        // secondary line to tell apart duplicates like Srinagar (J&K) vs
        // Srinagar (Uttarakhand). The TBO city code (used to live in
        // square brackets) was dropped from the label - it's a plumbing
        // detail with no operational value on the picker face; still
        // available on the row itself via `c.tboCityId` if any consumer
        // needs it.
        const airport = c.iataCode ? ` (${c.iataCode})` : '';
        const country = c.country?.name ? `, ${c.country.name}` : '';
        return {
          value: c.id,
          label: `${c.name}${airport}${country}`,
          description: c.state?.name ?? undefined,
        };
      },
    },
    value,
  );

  return (
    <UzCombobox
      label={label}
      placeholder={placeholder}
      searchPlaceholder="Search cities..."
      value={value ?? ''}
      onValueChange={(v) => onValueChange(v || null)}
      onSearchChange={setSearch}
      options={options}
      loading={loading}
      required={required}
      disabled={disabled}
      error={error}
      hint={hint}
    />
  );
};
